import { supabase } from '../../../shared/lib/supabaseClient';
import { callStudentRpc } from '../../auth/services/studentAuthService';
import { getOwnStudent, getSubjectsForStudent } from '../../StudentRecord/services/studentRecordService';

// Null when the signed-in account isn't a student, or isn't designated class secretary of
// their section. Otherwise their own student row plus the subjects taught to their section.
export async function getOwnSecretaryStatus() {
  const student = await getOwnStudent();
  if (!student?.is_secretary) return null;

  const subjects = await getSubjectsForStudent(student);
  return { student, subjects };
}

// One subject a secretary manages attendance for, scoped to the roster of their own section
// only (a subject can be taught to other sections too, which they don't get to touch).
export async function getSecretarySubject(subjectId) {
  const status = await getOwnSecretaryStatus();
  if (!status || !status.subjects.some((s) => s.subjectId === subjectId)) return null;

  const { data: subject, error } = await supabase
    .from('subjects')
    .select('id, name, category, grade_level')
    .eq('id', subjectId)
    .single();
  if (error) throw error;

  const { data: roster, error: rosterError } = await supabase
    .from('students')
    .select('id, lrn, firstname, lastname, middlename, section')
    .eq('grade_level', status.student.grade_level)
    .eq('section', status.student.section)
    // Attendance can't be recorded for students still waiting for super admin approval.
    .eq('approved', true)
    .order('lastname', { ascending: true })
    .order('firstname', { ascending: true });
  if (rosterError) throw rosterError;

  return { subject, roster };
}

// The secretary's stand-ins for gradesService's attendance functions (same arguments and results),
// for AttendancePanel. They go through the secretary_* database functions, which only touch the
// secretary's own section.
export const secretaryAttendanceService = {
  async getAttendanceForDate(subjectId, date) {
    return callStudentRpc('secretary_get_attendance', { p_subject_id: subjectId, p_date: date });
  },

  async saveAttendance(subjectId, date, term, records) {
    await callStudentRpc('secretary_save_attendance', {
      p_subject_id: subjectId,
      p_date: date,
      p_term: term,
      p_records: records.map(({ student_id, status }) => ({ student_id, status })),
    });
  },

  async resetAttendance(subjectId, date, studentIds) {
    if (studentIds.length === 0) return;
    await callStudentRpc('secretary_reset_attendance', {
      p_subject_id: subjectId,
      p_date: date,
      p_student_ids: studentIds,
    });
  },
};
